import { useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, Monitor, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { htmlHasUnsubscribe } from '../model/beefree-document';
import { applyMerge } from '@/features/marketing-shared';
import type { EmailBlock, TemplateEditor } from '../model/types';

interface EmailPreviewProps {
  subject: string;
  previewText: string;
  blocks: EmailBlock[];
  storeName: string;
  /** Nome do remetente; sem ele, o nome da loja. */
  fromName?: string;
  fromEmail?: string;
  editor?: TemplateEditor;
  html?: string;
  /** Exemplos para variáveis do escopo (`{{cor_fundo}}`, `{{logo}}`...), além dos campos de marketing. */
  samples?: Record<string, string>;
  /** `card` mostra só a folha, para a grade da biblioteca. */
  variant?: 'full' | 'card';
}

/** Largura CSS do celular: o e-mail renderiza nessa viewport, como no app de e-mail. */
const MOBILE_WIDTH = 320;
/** Acima disso o próprio corpo rola, como no cliente de e-mail. */
const MAX_HEIGHT = { desktop: 720, mobile: 520, card: 288 } as const;
const TIME = '09:41';

function stripActiveHtml(html: string): string {
  return html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, '')
    .replace(/<(object|embed|link|meta)\b[^>]*\/?>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/javascript:/gi, '');
}

const BASE_STYLE =
  '<style>html,body{margin:0;padding:0}body{font-family:sans-serif;color:#18181b;font-size:14px;line-height:1.5;background:#fff}img{max-width:100%;height:auto}</style>';

/**
 * Documento do iframe. Template completo (com `<html>`) entra como está, só com
 * o estilo base no `<head>`: fundo, largura e cores são os dele, como no e-mail
 * de verdade. Fragmento ganha a casca mínima.
 */
function previewDocument(body: string): string {
  const safe = stripActiveHtml(body);
  if (/<html[\s>]/i.test(safe)) {
    if (/<head[\s>]/i.test(safe)) return safe.replace(/<head[^>]*>/i, `$&<meta charset="utf-8">${BASE_STYLE}`);
    return safe.replace(/<html[^>]*>/i, `$&<head><meta charset="utf-8">${BASE_STYLE}</head>`);
  }
  return `<!DOCTYPE html><html><head><meta charset="utf-8">${BASE_STYLE}</head><body>${safe}</body></html>`;
}

/** iframe com a altura do conteúdo (até o teto), para o e-mail aparecer inteiro. */
function EmailFrame({
  doc,
  width,
  maxHeight,
  interactive,
}: {
  doc: string;
  width?: number;
  maxHeight: number;
  interactive: boolean;
}) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(0);
  const measure = () => {
    const inner = ref.current?.contentDocument;
    const next = Math.max(inner?.documentElement?.scrollHeight ?? 0, inner?.body?.scrollHeight ?? 0);
    if (next > 0) setHeight(next);
  };
  return (
    <iframe
      ref={ref}
      title="Prévia do e-mail"
      // Sem allow-scripts nada executa (e o HTML já vem sem script).
      // allow-same-origin só serve para medir a altura do conteúdo.
      sandbox="allow-same-origin"
      srcDoc={doc}
      onLoad={measure}
      className={cn('block border-0 bg-white', !interactive && 'pointer-events-none')}
      style={{ width: width ?? '100%', height: Math.min(Math.max(height, 160), maxHeight) }}
    />
  );
}

function BlockView({ block, text }: { block: EmailBlock; text: string }) {
  if (block.kind === 'divider') return <div className="h-px bg-zinc-200" />;
  if (!text && block.kind !== 'image') return null;
  if (block.kind === 'header') {
    return <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{text}</p>;
  }
  if (block.kind === 'hero') return <h3 className="text-xl font-semibold tracking-tight text-zinc-900">{text}</h3>;
  if (block.kind === 'image') {
    return (
      <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 px-3 py-8 text-center text-xs text-zinc-500">
        {text || 'Imagem'}
      </div>
    );
  }
  if (block.kind === 'button') {
    return (
      <span className="inline-flex rounded-lg bg-[#f5b400] px-4 py-2 text-sm font-medium text-[#111111]">{text}</span>
    );
  }
  if (block.kind === 'product') {
    return <div className="rounded-lg border border-zinc-200 px-3 py-2 text-sm font-medium text-zinc-900">{text}</div>;
  }
  if (block.kind === 'social') return <p className="text-xs text-zinc-500">{text}</p>;
  return <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-800">{text}</p>;
}

function UnsubscribeControl({ interactive }: { interactive: boolean }) {
  if (!interactive) {
    return <span className="text-xs text-zinc-500 underline underline-offset-2">Cancelar inscrição</span>;
  }
  return (
    <Button
      type="button"
      variant="link"
      className="h-auto px-0 text-xs text-zinc-500"
      onClick={() =>
        toast.message('Descadastro em um clique', {
          description: 'Na mensagem real, este link tira a pessoa da lista. Aqui é só a prévia.',
        })
      }
    >
      Cancelar inscrição
    </Button>
  );
}

function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center rounded-full bg-zinc-200 font-semibold text-zinc-600',
        className,
      )}
    >
      {name.trim().charAt(0).toUpperCase() || 'F'}
    </span>
  );
}

/** Janela de webmail: a linha da caixa de entrada (onde o texto de prévia aparece) e a mensagem aberta. */
function DesktopClient({
  subject,
  previewText,
  store,
  fromEmail,
  children,
}: {
  subject: string;
  previewText: string;
  store: string;
  fromEmail: string;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white text-zinc-900 shadow-sm">
      <div className="flex items-center gap-1.5 border-b border-zinc-200 bg-zinc-50 px-3 py-2">
        <span className="size-2.5 rounded-full bg-zinc-300" />
        <span className="size-2.5 rounded-full bg-zinc-300" />
        <span className="size-2.5 rounded-full bg-zinc-300" />
        <span className="ml-3 min-w-0 truncate text-[11px] text-zinc-500">{subject} – Caixa de entrada</span>
      </div>
      <div className="flex items-center gap-3 border-b border-zinc-200 bg-zinc-50/60 px-4 py-2 text-xs">
        <span className="w-28 shrink-0 truncate font-semibold text-zinc-800">{store}</span>
        <span className="min-w-0 flex-1 truncate">
          <span className="font-semibold text-zinc-800">{subject}</span>
          {previewText ? <span className="text-zinc-500"> – {previewText}</span> : null}
        </span>
        <span className="shrink-0 text-zinc-500">{TIME}</span>
      </div>
      <div className="px-5 pb-3 pt-4">
        <h3 className="text-lg leading-snug text-zinc-900">{subject}</h3>
        <div className="mt-3 flex items-start gap-3">
          <Avatar name={store} className="size-9 text-sm" />
          <div className="min-w-0 flex-1 text-xs">
            <p className="truncate">
              <span className="text-sm font-semibold text-zinc-900">{store}</span>{' '}
              <span className="text-zinc-500">&lt;{fromEmail}&gt;</span>
            </p>
            <p className="text-zinc-500">para mim</p>
          </div>
          <span className="shrink-0 text-xs text-zinc-500">{TIME}</span>
        </div>
      </div>
      {children}
    </div>
  );
}

/** App de e-mail no celular: a largura de dentro é a viewport do e-mail. */
function MobileClient({
  subject,
  store,
  fromEmail,
  children,
}: {
  subject: string;
  store: string;
  fromEmail: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto box-border max-w-full" style={{ width: MOBILE_WIDTH + 20 }}>
      <div className="rounded-[2.25rem] border-[10px] border-zinc-800 bg-zinc-800 shadow-xl dark:border-zinc-700">
        <div className="relative overflow-hidden rounded-[1.6rem] bg-white text-zinc-900">
          <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-zinc-800" />
          <div className="flex h-11 items-end justify-between px-5 pb-1 pt-3 text-[10px] font-medium text-zinc-600">
            <span>9:41</span>
            <span>●●●</span>
          </div>
          <div className="flex items-center gap-2 border-b border-zinc-200 px-3 py-2">
            <ChevronLeft className="size-4 shrink-0 text-zinc-500" />
            <p className="min-w-0 flex-1 truncate text-sm font-semibold">{subject}</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 text-xs">
            <Avatar name={store} className="size-8 text-xs" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{store}</p>
              <p className="truncate text-zinc-500">{fromEmail}</p>
            </div>
            <span className="shrink-0 text-[11px] text-zinc-500">{TIME}</span>
          </div>
          {children}
          <div className="flex justify-center bg-white py-2">
            <div className="h-1 w-28 rounded-full bg-zinc-300" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function EmailPreview({
  subject,
  previewText,
  blocks,
  storeName,
  fromName,
  fromEmail = 'novidades@news.exemplo.com.br',
  html = '',
  samples,
  variant = 'full',
}: EmailPreviewProps) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const fill = (value: string) =>
    samples ? value.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (token, key: string) => samples[key] ?? token) : value;
  const merge = (value: string) => applyMerge(fill(value), storeName);
  const mergedSubject = merge(subject).trim() || 'Sem assunto';
  const mergedPreview = merge(previewText).trim();
  const mergedHtml = merge(html);
  const showHtml = mergedHtml.trim().length > 0;
  const showFooter = !showHtml || !htmlHasUnsubscribe(mergedHtml);
  const compact = variant === 'card';
  const store = storeName.trim() || 'Sua loja';
  const sender = fromName?.trim() || store;
  const doc = showHtml ? previewDocument(mergedHtml) : '';

  const body = (maxHeight: number, width?: number) => (
    <>
      {showHtml ? (
        <EmailFrame key={width ?? 'desktop'} doc={doc} width={width} maxHeight={maxHeight} interactive={!compact} />
      ) : (
        <div className={cn('bg-white px-5 py-6', compact && 'pointer-events-none')}>
          <p className="text-center text-sm font-semibold tracking-tight text-zinc-900">{store}</p>
          <div className="mt-4 space-y-3 text-center">
            {blocks.map((block) => (
              <BlockView key={block.id} block={block} text={merge(block.text)} />
            ))}
          </div>
        </div>
      )}
      {showFooter ? (
        <div className={cn('border-t border-zinc-200 bg-white px-5 py-3 text-center', compact && 'pointer-events-none')}>
          <UnsubscribeControl interactive={!compact} />
        </div>
      ) : null}
    </>
  );

  if (compact) {
    return (
      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white text-zinc-900">{body(MAX_HEIGHT.card)}</div>
    );
  }

  return (
    <div className="min-w-0 space-y-3">
      <div>
        <p className="mb-2 text-sm font-medium">Ver como</p>
        <SegmentedControl
          size="sm"
          value={device}
          onChange={setDevice}
          options={[
            { value: 'desktop', label: 'Computador', icon: Monitor },
            { value: 'mobile', label: 'Celular', icon: Smartphone },
          ]}
        />
      </div>
      <div className="rounded-2xl bg-zinc-100 p-3 sm:p-5 dark:bg-zinc-900/60">
        {device === 'mobile' ? (
          <MobileClient subject={mergedSubject} store={sender} fromEmail={fromEmail}>
            {body(MAX_HEIGHT.mobile, MOBILE_WIDTH)}
          </MobileClient>
        ) : (
          <div className="mx-auto w-full max-w-3xl">
            <DesktopClient subject={mergedSubject} previewText={mergedPreview} store={sender} fromEmail={fromEmail}>
              {body(MAX_HEIGHT.desktop)}
            </DesktopClient>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Remetente fictício. Os campos entre chaves viram o exemplo da Ana.</p>
    </div>
  );
}
